import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { PenLine, Loader2, MapPin, X, ImagePlus, Trash2 } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Landmarks", "Hidden Gems", "Vanished Voices", "Wild Echoes", "City Pulses"];
const CATEGORY_DESCRIPTIONS = {
  "Landmarks": "Architectural Giants — The world-famous \"must-sees\" and engineering feats.",
  "Hidden Gems": "The Secrets — Mysterious or tucked-away details that most people walk past.",
  "Vanished Voices": "Lost Places & Events — Historic events or structures no longer physically there.",
  "Wild Echoes": "Parks & Natural Landscapes — Parks, waterfronts, geology, and wildlife.",
  "City Pulses": "Living Culture — The cultural movements, traditions, and community identities that make each neighborhood unique.",
};

export default function CreateStory({ onCreated, onClose }) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Landmarks");
  const [naId, setNaId] = useState("");
  const [location, setLocation] = useState(null); // { lat, lng, address?, place_name? } | null
  const [photos, setPhotos] = useState([]); // [{file, preview}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!user || user.role !== "echo_maker") return null;

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 10)); // Max 10 photos
    e.target.value = "";
  };

  const handlePhotoRemove = (id) => {
    setPhotos(prev => {
      const target = prev.find(p => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter(p => p.id !== id);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !content.trim()) {
      setError("Title and content are required");
      return;
    }
    setLoading(true);
    try {
      // 1. Create the story
      const storyRes = await axios.post(`${API}/stories`, {
        title: title.trim(),
        content: content.trim(),
        category,
        na_id: naId.trim() || null,
        original_image_url: null,
        location_coordinates: location || null,
      });

      const storyId = storyRes.data.id || storyRes.data._id;

      // 2. Upload photos and attach to story
      if (photos.length > 0 && storyId) {
        for (const photo of photos) {
          const formData = new FormData();
          formData.append("file", photo.file);
          await axios.post(`${API}/stories/${storyId}/media`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      // Cleanup previews
      photos.forEach(p => URL.revokeObjectURL(p.preview));
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create echoe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="create-story-modal">
      <div className="modal-content max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E2D9]">
          <div className="flex items-center gap-2">
            <PenLine size={18} className="text-[#B85C38]" />
            <h2 className="font-serif text-xl font-medium text-[#1A1A1A]">Create New Echoe</h2>
          </div>
          <button className="btn-ghost p-2" onClick={onClose} data-testid="close-create-story">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="create-story-error">
              {error}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`filter-chip ${category === cat ? "active" : ""}`}
                  onClick={() => setCategory(cat)}
                  data-testid={`create-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  title={CATEGORY_DESCRIPTIONS[cat]}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
              placeholder="Give your echoe a title..."
              data-testid="create-story-title"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">Your Narrative</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={5}
              className="w-full px-3 py-2.5 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none resize-none"
              placeholder="Tell the story behind this echoe — what did you discover, see, or feel in this place?"
              data-testid="create-story-content"
              required
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-2">
              Photos ({photos.length}/10)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {photos.map((photo) => (
                <div key={photo.id} className="relative w-20 h-20 border border-[#E5E2D9] overflow-hidden group">
                  <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    onClick={() => handlePhotoRemove(photo.id)}
                    data-testid={`remove-photo-${photo.id}`}
                  >
                    <Trash2 size={14} className="text-white" />
                  </button>
                </div>
              ))}
              {photos.length < 10 && (
                <label className="w-20 h-20 border-2 border-dashed border-[#E5E2D9] flex items-center justify-center cursor-pointer hover:border-[#B85C38] hover:bg-[#F4F1E6] transition-all" data-testid="add-photo-btn">
                  <ImagePlus size={20} className="text-[#718096]" />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoAdd}
                  />
                </label>
              )}
            </div>
            <p className="text-[10px] text-[#718096] font-mono">Click + to add photos. Supports JPG, PNG, GIF, WebP. Max 10MB each.</p>
          </div>

          {/* Location & Source */}
          <div className="border border-[#E5E2D9] p-4 bg-[#F9F7F1] space-y-3">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#718096]" />
              <span className="font-mono text-xs uppercase tracking-widest text-[#718096]">
                Location & Source (Optional)
              </span>
            </div>

            {/* Map picker */}
            <LocationPicker value={location} onChange={setLocation} height={260} />

            {/* Source ID / reference link */}
            <input
              type="text"
              value={naId}
              onChange={e => setNaId(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E2D9] bg-white text-xs focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
              placeholder="Source ID or reference link (e.g. NARA National Archives ID)"
              data-testid="create-story-naid"
            />
          </div>

          <button
            type="submit"
            className="btn-accent w-full justify-center py-3"
            disabled={loading}
            data-testid="submit-story-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Publish Echoe"}
          </button>
        </form>
      </div>
    </div>
  );
}
